import { api } from '@/store/api'
import type { PaginatedResponse } from '@/types/api'
import type { CreditPackage, PaymentMethod } from './credits/lib/paymentMethods'

// Регистр camelCase: /api/v1/ai-credits/** живёт по правилам обычных ручек
// приложения, в отличие от snake_case /api/v1/ai/**. Пакеты и покупка —
// под-проект G.
export interface CreditsBalance {
  balance: string
  /**
   * Заморожено незакрытыми резервами. Уже ВЫЧТЕНО из `balance`, и в историю
   * движений резерв не попадает по построению — без этого поля уменьшившийся
   * во время прогона баланс выглядит пропажей.
   */
  held: string
  /**
   * Сумма ПОСЛЕДНЕЙ покупки (бонусы и компенсации не входят), `null` — покупок
   * не было. Знаменатель полоски остатка: `balance / lastPurchaseAmount`, не
   * больше 100%. Необязательное — старый бэк поля не отдаёт.
   */
  lastPurchaseAmount?: string | null
}

/** Терминальные операции. `hold`/`release` бэк в список не кладёт по
 *  построению: резерв ещё не потрачен, а полный возврат означает прогон, за
 *  который с автора не взяли денег. Заморозку отдаёт `held` в балансе. */
export type CreditsOperation = 'purchase' | 'charge' | 'bonus' | 'refund'

/** `null` у пополнения и начисления. У списания — чем прогон был. */
export interface CreditsHistoryDetail {
  jobType: string | null
  prompt: string | null
  model: string | null
  imageUrl: string | null
}

export interface CreditsHistoryItem {
  id: number
  operation: CreditsOperation
  /** ВСЕГДА положительный: знак несёт `operation`. */
  amount: string
  /**
   * Снимок баланса на момент операции, а НЕ бегущий итог списка: он уже
   * уменьшен другими открытыми резервами, а строк резерва здесь нет. У автора
   * с двумя прогонами сразу соседние значения не сойдутся — гейт «одна джоба»
   * считает по КУРСУ, а не по автору. Столбец «остаток» из этого поля строить
   * нельзя; годится только внутри подробностей одной операции.
   */
  balanceAfter: string
  createdAt: string
  description: string | null
  detail: CreditsHistoryDetail | null
}

export interface CreditsHistoryArgs {
  page: number
  limit: number
}

/** Траты за день: `spent` — списания минус компенсации, бывает ОТРИЦАТЕЛЬНЫМ. */
export interface CreditsUsageDay {
  /** Локальная дата `YYYY-MM-DD` в поясе `tz` запроса. */
  date: string
  spent: string
}

export interface CreditsUsageArgs {
  days: number
  /** IANA-пояс браузера: сутки режутся по нему (иначе ночная трата уедет во вчера). */
  tz: string
}

export interface BuyCreditsRequest {
  packageId: number
  currency: PaymentMethod
}

/** `invoiceId` не используется ни одним экраном — показывать автору нечего. */
export interface BuyCreditsResponse {
  paymentUrl: string | null
  invoiceId: string | null
  transactionId: number
  processing: boolean
}

export const creditsApi = api.injectEndpoints({
  endpoints: (build) => ({
    getCreditsBalance: build.query<CreditsBalance, void>({
      query: () => 'ai-credits/balance',
      // Баланс списывается прогонами — протухает вместе со сметой, когда
      // JobPoller инвалидирует по терминальному статусу джобы.
      providesTags: ['CreditsBalance'],
    }),
    getCreditPackages: build.query<CreditPackage[], void>({
      query: () => 'ai-credits/packages',
      // Витрина не протухает ни от одного действия автора (бэк кеширует её на
      // час), поэтому тега нет вовсе — инвалидировать нечем и незачем.
      keepUnusedDataFor: 3600,
    }),
    getCreditsHistory: build.query<PaginatedResponse<CreditsHistoryItem>, CreditsHistoryArgs>({
      query: ({ page, limit }) => `ai-credits/transactions?page=${page}&limit=${limit}`,
      // Списание появляется ровно тогда же, когда меняется баланс — на
      // терминальном статусе джобы; пополнение — по подтверждению платежа.
      providesTags: ['CreditsHistory'],
    }),
    getCreditsUsage: build.query<CreditsUsageDay[], CreditsUsageArgs>({
      query: ({ days, tz }) =>
        `ai-credits/usage?days=${days}&tz=${encodeURIComponent(tz)}`,
      // Траты меняются вместе с историей движений — тем же тегом.
      providesTags: ['CreditsHistory'],
    }),
    buyCredits: build.mutation<BuyCreditsResponse, BuyCreditsRequest>({
      query: (body) => ({ url: 'ai-credits/buy', method: 'POST', body }),
      // Баланс НЕ инвалидируем: покупка только открывает воронку, деньги
      // приходят по подтверждению платежа (§1.4 — перечитывать по возврату,
      // не оптимистично).
    }),
  }),
})

export const {
  useGetCreditsBalanceQuery,
  useGetCreditPackagesQuery,
  useGetCreditsHistoryQuery,
  useGetCreditsUsageQuery,
  useBuyCreditsMutation,
} = creditsApi
